import Link from '@/routing/AppLink';
import { useTranslation } from 'next-i18next/pages';

import type { NavMenuLink } from '@/data/shared/navMenu';

import { resolveNavLinkAttributes } from '@/utils/resolveNavLinkAttributes';

import type { IconSizeType } from '@/types/icon';

import { ExternalLinkMark } from './ExternalLinkMark';

/**
 * ### NavMenuAnchorProps
 *
 * The navigation link to render and how the surrounding list styles it.
 */
export interface NavMenuAnchorProps {
  /**
   * ### link
   *
   * Declared navigation link; its label is translated here.
   */
  link: NavMenuLink;

  /**
   * ### className
   *
   * Classes for the anchor itself, typically the list's item style.
   */
  className?: string;

  /**
   * ### markSize
   *
   * Icon size of the external-link mark when the link leaves the site.
   *
   * @defaultValue `16` — matches 16px body text
   */
  markSize?: IconSizeType;
}

/**
 * ### NavMenuAnchor
 *
 * Renders one navigation link the same way everywhere: internal routes navigate
 * in place, external ones open in a new window with `rel` set and the
 * external-link mark appended to the label.
 * Active-state styling is the caller's concern and arrives through `className`.
 *
 * @param props Link to render and its styling
 * @returns The anchor element
 * @see {@link resolveNavLinkAttributes} Where target and rel are decided
 */
export function NavMenuAnchor({
  link,
  className,
  markSize = 16,
}: NavMenuAnchorProps) {
  const { t } = useTranslation('common');
  const { isExternal, target, rel } = resolveNavLinkAttributes(link);

  return (
    <Link
      href={link.url}
      className={className}
      target={target}
      rel={rel}
      prefetch={false}
    >
      <span className="inline-flex items-center gap-1">
        {t(link.label)}
        {isExternal && <ExternalLinkMark size={markSize} />}
      </span>
    </Link>
  );
}
